import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useShallow } from 'zustand/react/shallow';
import { useHeaderStore } from '@/store/useHeaderStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavCompact } from '@/hooks/use-nav-compact';
import { useTranslation } from 'react-i18next';
import { useHeaderBreadcrumb } from '@/hooks/useHeaderBreadcrumb';
import { getSidebarMenuIcon, resolveMenuIconTypeForPath } from '@/lib/navigation/sidebarMenuIcons';
import type { HeaderBreadcrumbItem } from '@/lib/navigation/headerBreadcrumbTypes';

/** Compact Attio-like size for every action passed as `useHeader({ children })`. */
const HEADER_ACTIONS_CLASSNAME =
  'flex shrink-0 items-center gap-2 [&_[data-slot=button]]:!h-8 [&_[data-slot=button]]:!min-h-8 [&_[data-slot=button]]:text-[13px] [&_[data-slot=button]]:leading-none [&_[data-slot=button]]:gap-1.5 [&_[data-slot=button]:not([class*="size-"])]:!px-2.5 [&_[data-slot=button]:has(>svg)]:!px-2.5 [&_[data-slot=button].size-9]:!size-8 [&_[data-slot=button].size-10]:!size-8';

const BREADCRUMB_LINK_CLASSNAME =
  'flex max-w-[min(100%,14rem)] shrink-0 cursor-pointer items-center gap-1.5 truncate text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100';

interface HeaderPageTitleProps {
  className?: string;
  subtitle?: string;
  subtitleTooltipClassName?: string;
  children: ReactNode;
}

function HeaderPageTitle({ className, subtitle, subtitleTooltipClassName, children }: HeaderPageTitleProps) {
  if (!subtitle) {
    return <h1 className={className}>{children}</h1>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <h1 className={cn(className, 'cursor-help')}>{children}</h1>
      </TooltipTrigger>
      <TooltipContent className={cn('max-w-sm whitespace-normal', subtitleTooltipClassName)}>
        <p>{subtitle}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function resolveBreadcrumbLabel(item: HeaderBreadcrumbItem, t: (key: string) => string): string {
  if (item.labelKey) {
    return t(item.labelKey);
  }
  return item.label ?? '';
}

const Header = () => {
  const { t } = useTranslation();
  const {
    text,
    subtitle,
    subtitleTooltipClassName,
    children,
    className,
    showBack,
    backTo,
    onBack,
    breadcrumbItems,
  } = useHeaderStore(
    useShallow((state) => ({
      text: state.text,
      subtitle: state.subtitle,
      subtitleTooltipClassName: state.subtitleTooltipClassName,
      children: state.children,
      className: state.className,
      showBack: state.showBack,
      backTo: state.backTo,
      onBack: state.onBack,
      breadcrumbItems: state.breadcrumbItems,
    }))
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isNavCompact = useNavCompact();
  const { parentLabelKey, parentTo } = useHeaderBreadcrumb();

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      navigate(backTo);
      return;
    }
    if (parentTo) {
      navigate(parentTo);
    }
  };

  const navigateBreadcrumbItem = (item: HeaderBreadcrumbItem) => {
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (item.to) {
      navigate(item.to);
    }
  };

  const prefixItems: HeaderBreadcrumbItem[] = useMemo(() => {
    if (breadcrumbItems?.length) {
      return breadcrumbItems;
    }
    if (showBack && parentLabelKey && parentTo) {
      return [{ labelKey: parentLabelKey, to: parentTo, showSectionIcon: true }];
    }
    return [];
  }, [breadcrumbItems, showBack, parentLabelKey, parentTo]);

  if (!text) return null;

  const isReactNode = typeof text !== 'string';
  const displayText = isReactNode ? text : capitalizeFirstLetter(text);
  const showBreadcrumbTrail = showBack && prefixItems.length > 0;
  const CurrentSectionIcon = !showBreadcrumbTrail
    ? getSidebarMenuIcon(resolveMenuIconTypeForPath(pathname))
    : null;

  return (
    <div
      className={cn(
        'flex justify-between gap-3 py-3 nav:py-2.5 border-b border-gray-200 px-4 sm:px-6 nav:px-10 min-w-0',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5 min-h-8">
        {isNavCompact && (
          <SidebarTrigger className="size-8 min-h-8 min-w-8 shrink-0 text-gray-700 hover:bg-gray-100" />
        )}
        {showBack && !showBreadcrumbTrail && (
          <button
            type="button"
            onClick={handleBack}
            aria-label={t('common.back')}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:text-gray-400"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
        )}
        <nav aria-label={t('common.breadcrumb')} className="flex min-w-0 flex-1 items-center gap-1">
          {showBreadcrumbTrail ? (
            <>
              {prefixItems.map((item, index) => {
                const label = resolveBreadcrumbLabel(item, t);
                const SectionIcon =
                  item.showSectionIcon && item.to
                    ? getSidebarMenuIcon(resolveMenuIconTypeForPath(item.to))
                    : null;
                return (
                  <span key={`${item.labelKey ?? item.label ?? item.to}-${index}`} className="flex shrink-0 items-center gap-1">
                    {index > 0 ? <ChevronRight className="size-3.5 shrink-0 text-gray-400" aria-hidden /> : null}
                    <button
                      type="button"
                      onClick={() => navigateBreadcrumbItem(item)}
                      className={BREADCRUMB_LINK_CLASSNAME}
                    >
                      {SectionIcon ? (
                        <SectionIcon
                          className="size-3.5 shrink-0 text-gray-500 dark:text-gray-400"
                          strokeWidth={2}
                          aria-hidden
                        />
                      ) : null}
                      <span className="truncate">{label}</span>
                    </button>
                  </span>
                );
              })}
              <ChevronRight className="size-3.5 shrink-0 text-gray-400" aria-hidden />
              <HeaderPageTitle
                className="min-w-0 truncate text-sm font-semibold text-gray-900 sm:text-base dark:text-gray-100"
                subtitle={subtitle}
                subtitleTooltipClassName={subtitleTooltipClassName}
              >
                {displayText}
              </HeaderPageTitle>
            </>
          ) : (
            <div className="flex min-w-0 items-center gap-1.5">
              {CurrentSectionIcon ? (
                <CurrentSectionIcon
                  className="size-[18px] shrink-0 text-gray-900 dark:text-gray-100"
                  strokeWidth={2}
                  aria-hidden
                />
              ) : null}
              <HeaderPageTitle
                className="min-w-0 truncate text-base font-semibold sm:text-lg"
                subtitle={subtitle}
                subtitleTooltipClassName={subtitleTooltipClassName}
              >
                {displayText}
              </HeaderPageTitle>
            </div>
          )}
        </nav>
      </div>
      <div className={HEADER_ACTIONS_CLASSNAME}>{children}</div>
    </div>
  );
};

export default Header;
